/**
 * Reading the garden's plants: all of them, one, or the archived ones.
 *
 * One endpoint (GET /plants) answers all three, told apart by its query.
 * A ZIP, when given, has the server merge each crop's temperatures with the
 * garden's climate. Writing plants is plantCatalogue's.
 *
 * Endpoint: GET /plants[?plantObjId=][&zip=][&status=0][&lang=] → Plant[] (a
 * bare list). Whose plants come back is decided by the token, or, in a shared
 * garden, by X-Grow-Garden (its gardenContext). Left out, status means
 * growing plants only; status=0 asks for the archive. In a shared garden no
 * ?zip is sent (gardenMembers.gardenZip), so the backend reads the garden at
 * its owner's postcode. lang is the app's language, so each plant's names
 * (services/grow/plantNaming) come in it.
 *
 * Who uses it: shared/context/PlantsContext (the garden's plants for every
 * screen) and the archived lists in features/plant-list, locations and
 * season. Caching: [PLANTS_QUERY_KEY, userId, zip || 'none'] ('plants') in
 * PlantsContext and [ARCHIVED_PLANTS_QUERY_KEY, userId] ('archived-plants')
 * for the archived, both the default five minutes. Everything that changes a
 * plant invalidates (or updates in place) 'plants'.
 *
 * @module api/plants
 */

import { apiClient } from './apiClient';
import { gardenZip } from './gardenMembers';
import { Plant } from '../types/plant';
import { getLanguage } from '@/shared/i18n/i18nSetup';

/**
 * The query of GET /plants: the ZIP when there is one, and the language the
 * names come in (the app's, so a plant is named as the screen speaks).
 *
 * @param zip - The signed-in person's own postcode, if any (gardenZip).
 * @param lang - The names' language; the app's when not given.
 * @param rest - The other query fields (plantObjId, status).
 * @returns The query, without its leading "?".
 */
function plantsQuery(zip: string | undefined, lang: string | undefined, rest: Record<string, string> = {}): string {
  const trimmedZip = gardenZip(zip);
  const params = new URLSearchParams({ ...rest, lang: lang ?? getLanguage() });
  if (trimmedZip) params.set('zip', trimmedZip);
  return params.toString();
}

/**
 * Fetches the garden's growing plants. When zip is provided, crop temps use the GGP merge.
 *
 * @param zip - The signed-in person's own 5-digit US postal code, if any; sent only in their own garden (gardenZip).
 * @param lang - The language the plants' names come in; the app's when not given.
 * @returns The plants, whole (photos, notes, stages, projections, names).
 */
export const getPlants = async (zip?: string, lang?: string): Promise<Plant[]> => {
  const response = await apiClient.get(`/plants?${plantsQuery(zip, lang)}`);
  return response.data;
};

/**
 * Fetches a single plant by plant_obj id.
 *
 * @param plantObjId - Plant object id.
 * @param zip - The signed-in person's own 5-digit US postal code, if any; sent only in their own garden (gardenZip).
 * @param lang - The language the plant's names come in; the app's when not given.
 * @returns The plant.
 * @throws When the garden has no such growing plant.
 */
export const getSinglePlant = async (plantObjId: string, zip?: string, lang?: string): Promise<Plant> => {
  const response = await apiClient.get(`/plants?${plantsQuery(zip, lang, { plantObjId })}`);
  const plants: Plant[] = response.data;
  if (!plants.length) {
    throw new Error(`Plant not found: ${plantObjId}`);
  }
  return plants[0];
};
/**
 * Plants the gardener has taken out of the garden.
 *
 * Removing a plant sets its status rather than deleting it, so these rows are
 * whole — photos, notes and timeline included — and can be restored.
 *
 * Whose they are is decided by the token and X-Grow-Garden.
 *
 * @param lang - The language the plants' names come in; the app's when not given.
 * @returns The archived plants.
 */
export const getArchivedPlants = async (lang?: string): Promise<Plant[]> => {
  const response = await apiClient.get(`/plants?${plantsQuery(undefined, lang, { status: '0' })}`);
  return response.data;
};
